import { Controller } from "@hotwired/stimulus";

/**
 * "On this page" rail (Section I5).
 *
 * Lists the sub-headings of the top-level section currently in view and
 * highlights the heading the reader is looking at via an IntersectionObserver.
 * The docs are a single long page, so the active section is recomputed on
 * scroll; when it changes, the rail is rebuilt for that section's headings.
 *
 * Many sub-headings have no id to link to, so we slugify + assign one on the fly
 * (deduped against the document). Generated ids inherit the global
 * `scroll-margin-top` rule, so anchor jumps clear the sticky header.
 */
export default class extends Controller {
  static targets = ["list"];

  connect() {
    this._inner = document.querySelector(".docs-content-inner");
    this._sections = this._inner
      ? Array.from(this._inner.querySelectorAll(":scope > section[id]"))
      : [];

    if (!this._sections.length) {
      this._hide();
      return;
    }

    this._activeSectionId = null;
    this._headingObserver = null;
    this._visible = new Set();
    this._headings = [];
    this._ticking = false;

    this._boundScroll = this._onScroll.bind(this);
    window.addEventListener("scroll", this._boundScroll, { passive: true });
    window.addEventListener("resize", this._boundScroll, { passive: true });

    this._updateActiveSection();
  }

  disconnect() {
    window.removeEventListener("scroll", this._boundScroll);
    window.removeEventListener("resize", this._boundScroll);
    if (this._headingObserver) this._headingObserver.disconnect();
  }

  // ── Active-section tracking ──────────────────────────────────────────────────

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._updateActiveSection();
      this._ticking = false;
    });
  }

  _updateActiveSection() {
    const offset = 100;
    let active = this._sections[0];
    for (const section of this._sections) {
      if (section.getBoundingClientRect().top <= offset) active = section;
    }
    if (!active || active.id === this._activeSectionId) return;

    this._activeSectionId = active.id;
    this._buildRail(active);
  }

  // ── Rail construction ────────────────────────────────────────────────────────

  _buildRail(section) {
    const headings = Array.from(section.querySelectorAll("h2, h3, h4")).filter(
      (h) =>
        !h.classList.contains("cs-title-main") &&
        !h.classList.contains("cs-title-section") &&
        h.textContent.trim()
    );

    if (this._headingObserver) {
      this._headingObserver.disconnect();
      this._headingObserver = null;
    }
    this._visible.clear();
    this.listTarget.textContent = "";

    if (!headings.length) {
      this._hide();
      this._headings = [];
      return;
    }

    headings.forEach((h) => {
      if (!h.id) h.id = this._uniqueSlug(h.textContent);
    });
    this._headings = headings;

    const frag = document.createDocumentFragment();
    headings.forEach((h) => {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.className = "docs-onpage-link docs-onpage-link--" + h.tagName.toLowerCase();
      a.href = "#" + h.id;
      a.textContent = h.textContent.trim();
      a.dataset.headingId = h.id;
      li.appendChild(a);
      frag.appendChild(li);
    });
    this.listTarget.appendChild(frag);
    this._show();

    this._observeHeadings(headings);
  }

  _observeHeadings(headings) {
    this._headingObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this._visible.add(entry.target.id);
          else this._visible.delete(entry.target.id);
        }
        this._highlightCurrent();
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 }
    );
    headings.forEach((h) => this._headingObserver.observe(h));
  }

  _highlightCurrent() {
    // Prefer the topmost heading currently in the highlight band; otherwise keep
    // the last heading scrolled above it so the rail always marks a position.
    let currentId = null;
    for (const h of this._headings) {
      if (this._visible.has(h.id)) {
        currentId = h.id;
        break;
      }
    }
    if (!currentId) {
      for (const h of this._headings) {
        if (h.getBoundingClientRect().top <= 100) currentId = h.id;
      }
    }

    this.listTarget.querySelectorAll(".docs-onpage-link").forEach((a) => {
      a.classList.toggle("is-active", a.dataset.headingId === currentId);
    });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  _uniqueSlug(text) {
    const base =
      "toc-" +
      text
        .trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
    let slug = base || "toc-section";
    let n = 2;
    while (document.getElementById(slug)) {
      slug = base + "-" + n;
      n += 1;
    }
    return slug;
  }

  _show() {
    this.element.hidden = false;
  }

  _hide() {
    this.element.hidden = true;
  }
}
